import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import { Box, Typography, Grid } from "@mui/material";
import PrimaryTextField from "../../Inputs/PrimaryTextField/PrimaryTextField";
import InfoIcon from "../../../assets/icons/InfoIcon";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import StyledTooltip from "../../StyledTooltip/StyledTooltip";
import SecurityCheck from "../../SecurityCheck/SecurityCheck";
import { formatCurrency, formatEmpty, formatNumberWithCommas, formatTitleCase } from "../../../utils/formatHelpers";
import { parseAndFormatDate } from "../../../utils/dateHelpers";
import { nullCheck } from "../../../utils/dataHelpers";
import { CarrierService } from "../../../services/CarrierService";
import { AlertContext } from "../../Dashboard/Dashboard";

export default function LoadDetails({
  activeLoad,
  setActiveLoad,
  maxPayInfo,
  maxPay,
  setMaxPay,
  onLoadFieldUpdate,
  handleTableAction
}) {
  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  const [stops, setStops] = useState([]);
  const [originLocation, setOriginLocation] = useState("");
  const [originDate, setOriginDate] = useState("");
  const [destinationLocation, setDestinationLocation] = useState("");
  const [destinationDate, setDestinationDate] = useState("");
  const [extraStops, setExtraStops] = useState("");
  const [tenderFullType, setTenderFullType] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleUpdateMaxPay = () => {
    const maxPayAmount = parseFloat(maxPay) || 0;
    if (activeLoad.maxPay === maxPayAmount) return;

    if (maxPayAmount >= 100) {
      const payload = {
        orderId: activeLoad.id,
        movementId: activeLoad.movementId,
        overrideMaxPay: maxPayAmount
      };

      setIsLoading(true);
      CarrierService.UpdateMaxPay(instance, payload).then(() => {
        const loadUpdated = activeLoad;
        loadUpdated.maxPay = maxPayAmount;
        setActiveLoad(loadUpdated);
        onLoadFieldUpdate?.(activeLoad.id, "maxPay", maxPayAmount);
        setIsLoading(false);
      }).catch(error => {
        setIsLoading(false);
        console.error(error);
        warning(error);
      });
    } else {
      setMaxPay(activeLoad.maxPay ?? 0);
      alert("Error, Minimum 3-Digits required");
    }
  };

  useEffect(() => {
    if (stops?.length) {
      const originStop = stops[0];
      const destinationStop = stops[stops.length - 1];

      setOriginDate(parseAndFormatDate(originStop.schedArriveEarly, "MM/dd/yy HH:mm"));
      setDestinationDate(parseAndFormatDate(destinationStop.schedArriveEarly, "MM/dd/yy HH:mm"));
      setOriginLocation(`${formatTitleCase(originStop.cityName)}, ${originStop.state}`);
      setDestinationLocation(`${formatTitleCase(destinationStop.cityName)}, ${destinationStop.state}`);
      setExtraStops(stops.length > 2 ? (`+${stops.length - 2} stop(s)`) : "");
    }
  }, [stops]);

  useEffect(() => {
    if (activeLoad) {
      setStops(activeLoad.stops);
      setMaxPay(activeLoad.maxPay);

      switch (activeLoad.tender) {
        case "F":
          setTenderFullType("Fixed");
          break;
        case "S":
          setTenderFullType("Spot");
          break;
        case "V":
          setTenderFullType("Volume");
          break;
        default:
          setTenderFullType(activeLoad.tender);
      }
    }
  }, [activeLoad]);

  return (
    <Box
      sx={{
        padding: "0 20px 20px 20px",
        backgroundColor: "black_solid.05",
        color: "black_solid.80"
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <Box>
          <Box sx={{ display: "flex", marginBottom: "5px", alignItems: "center" }}>
            <Typography
              variant="h1"
              color="primary.60"
              sx={{ cursor: "pointer" }}
              onClick={e => {
                e.stopPropagation();
                handleTableAction({ action: "load-profile", loadId: activeLoad.id });
              }}
            >
              {`${activeLoad.id}`}
            </Typography>
            <Typography variant="h1" sx={{ marginLeft: "10px", color: "black_opacity.100" }}>
              {originLocation}
            </Typography>
            <ArrowForwardIcon sx={{ marginLeft: "10px", fontSize: "30px" }} />
            <Typography variant="h1" sx={{ marginLeft: "10px", color: "black_opacity.100" }}>
              {destinationLocation}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", color: "black_opacity.70" }} >
            {extraStops && (
              <>
                <Typography variant="h4">
                  {extraStops}
                </Typography>
                <Typography variant="h4" sx={{ marginX: "10px" }}>&bull;</Typography>
              </>
            )}
            {activeLoad.miles > 0 && (
              <>
                <Typography variant="h4">
                  {activeLoad.miles} mi
                </Typography>
                <Typography variant="h4" sx={{ marginX: "10px" }}>&bull;</Typography>
              </>
            )}
            {activeLoad.weight > 0 &&
              <Typography variant="h4">
                {formatNumberWithCommas(activeLoad.weight)} lbs
              </Typography>
            }
          </Box>
        </Box>
        <Box sx={{ display: "flex", marginLeft: "auto" }}>
          <Box>
            <Typography variant="subhead1" color="black_opacity.100">
              Scheduled Pick Up
            </Typography>
            <Typography variant="body1" color="black_opacity.100">
              {originDate}
            </Typography>
          </Box>
          <Box sx={{ marginLeft: "10px" }}>
            <Typography variant="subhead1" color="black_opacity.100">
              Scheduled Drop Off
            </Typography>
            <Typography variant="body1" color="black_opacity.100">
              {destinationDate}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="subhead1" color="black_opacity.100">
              Customer
            </Typography>
            <Typography variant="body1" color="black_opacity.100">
              {nullCheck(activeLoad.customer?.name, "-")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="subhead1" color="black_opacity.100">
              Tender
            </Typography>
            <Typography variant="body1" color="black_opacity.100">
              {nullCheck(tenderFullType, "-")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="subhead1" color="black_opacity.100">
              CFM AM
            </Typography>
            <Typography variant="body1" color="black_opacity.100">
              {formatEmpty(activeLoad.accountManager)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="subhead1" color="black_opacity.100" display="flex">
              Max Pay
              {maxPayInfo.isRevenueAdjustment &&
                <StyledTooltip title={`DAT: ${formatEmpty(formatCurrency(maxPayInfo.datRateview, 2))}`}>
                  <Box sx={{ height: "20px", marginLeft: "5px" }}>
                    <InfoIcon
                      sx={{
                        fontSize: "20px",
                        color: "error.60",
                      }}
                    />
                  </Box>
                </StyledTooltip>
              }
            </Typography>
            <SecurityCheck
              componentName="CarrierOffersDashboardManager"
              action="Read">
              <span style={{ display: "flex", paddingRight: "20px" }}>
                <PrimaryTextField
                  value={maxPay}
                  onChange={setMaxPay}
                  onBlur={handleUpdateMaxPay}
                  isNumber
                  isCurrency
                  disabled={isLoading}
                  error={!maxPay}
                />
              </span>
            </SecurityCheck>
            <SecurityCheck
              componentName="CarrierOffersDashboardRep"
              action="Read">
              <Typography variant="body1" color="black_opacity.100">
                {formatCurrency(maxPay)}
              </Typography>
            </SecurityCheck>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="subhead1" color="black_opacity.100">
              Cargo Value
            </Typography>
            <Typography variant="body1" color="black_opacity.100">
              {formatCurrency(Math.floor(activeLoad.cargoValue))}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="subhead1" color="black_opacity.100">
              Commodity
            </Typography>
            <Typography variant="body1" color="black_opacity.100">
              {nullCheck(formatTitleCase(activeLoad.commodity), "-")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="subhead1" color="black_opacity.100">
              Mode
            </Typography>
            <Typography variant="body1" color="black_opacity.100">
              {activeLoad.revenueCodeId} ({activeLoad.equipmentTypeId}) {typeof (activeLoad.minTemp) == "number" && <span>{activeLoad.minTemp} / {activeLoad.maxTemp}</span>}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="subhead1" color="black_opacity.100">
              CFM OPS
            </Typography>
            <Typography variant="body1" color="black_opacity.100">
              {formatEmpty(activeLoad.operationsRep)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="subhead1" color="black_opacity.100">
              Revenue
            </Typography>
            <Typography variant="body1" color="black_opacity.100">
              {formatCurrency(activeLoad.totalCharge)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="subhead1" color="black_opacity.100">
              DAT Rate
            </Typography>
            <Typography variant="body1" color="black_opacity.100">
              {formatEmpty(formatCurrency(maxPayInfo?.datRateview, 2))}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

LoadDetails.propTypes = {
  activeLoad: PropTypes.object.isRequired,
  setActiveLoad: PropTypes.func.isRequired,
  maxPayInfo: PropTypes.object.isRequired,
  maxPay: PropTypes.number.isRequired,
  setMaxPay: PropTypes.func.isRequired,
  onLoadFieldUpdate: PropTypes.func,
  handleTableAction: PropTypes.func
};